import { useMemo } from 'react';

import moment from 'moment';

import type { Invitation, SigninRequirements, SiteWithPermissions } from '@sine/sine-core-api-types';

import securityPermissions from '../../../helpers/securityPermissions';
import type { InitialInvitationDetailsValuesWithoutDynamicValues } from '../Invitations.types';
import { isHostRequired } from '../helpers/isHostRequired';
import { isZonesRequired } from '../helpers/isZonesRequired';
import { generateCreateInvitationsSchema } from '../invitations.schemas';

interface UseCreateInvitationRequirementsProps {
  invitation?: Invitation;
  selectedSite: SiteWithPermissions;
  selectedVisitorTypeId: string;
  siteRequirements?: SigninRequirements;
  isLoading: boolean;
}

export function useCreateInvitationRequirements({
  invitation,
  selectedSite,
  selectedVisitorTypeId,
  siteRequirements,
  isLoading,
}: UseCreateInvitationRequirementsProps) {
  let requireHostSelection = false;
  let requireZoneSelection = false;

  if (!isLoading && siteRequirements) {
    requireHostSelection = isHostRequired(siteRequirements, selectedVisitorTypeId);
    requireZoneSelection = isZonesRequired(siteRequirements);
  }

  const schema = useMemo(
    () => generateCreateInvitationsSchema(requireHostSelection, !!invitation, requireZoneSelection),
    [invitation, requireHostSelection, requireZoneSelection],
  );

  const visitorTypeOptions = useMemo(() => {
    return (siteRequirements?.visitorTypes || [])
      .filter(
        (visitorType) =>
          securityPermissions.checkPermissions(selectedSite.permissions, 'SIGN_IN') ||
          (selectedSite.settings.hostByVisitorTypeRequiredForSignIn || []).includes(visitorType.id),
      )
      .map((vt) => ({
        label: vt.name,
        value: vt.id,
      }));
  }, [
    selectedSite.permissions,
    selectedSite.settings.hostByVisitorTypeRequiredForSignIn,
    siteRequirements?.visitorTypes,
  ]);

  const hostOptions = useMemo(() => {
    if (!siteRequirements?.hostGroups) {
      return [];
    }

    return siteRequirements.hostGroups
      .flatMap((hg) => {
        if (!hg.hosts) {
          return [];
        }
        return hg.hosts.map((host) => ({
          label: `${host.firstName} ${host.lastName} (${hg.name})`,
          value: host.id,
        }));
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [siteRequirements?.hostGroups]);

  const initialValues: InitialInvitationDetailsValuesWithoutDynamicValues = useMemo(
    () => ({
      invitee: {
        firstName: invitation?.firstName || '',
        lastName: invitation?.lastName || '',
        email: invitation?.email || '',
        mobile: invitation?.mobile || '',
      },
      invitationDetails: {
        company: invitation?.companyDetails?.name || '',
        subject: invitation?.subject || '',
        adminNotes: invitation?.adminNotes || '',
        expiry: invitation
          ? invitation.expiry
          : {
              type: 'elapsed',
              settings: {
                value: 1,
                unit: 'hours',
              },
            },
        validFrom: invitation ? new Date(invitation.startDate) : moment().startOf('hour').add(1, 'hour').toDate(),
        validTo: invitation ? new Date(invitation.endDate) : moment().startOf('hour').add(2, 'hour').toDate(),
        visitorNotes: invitation?.visitorNotes || '',
      },
    }),
    [invitation],
  );

  return { schema, initialValues, visitorTypeOptions, hostOptions, requireHostSelection, requireZoneSelection };
}
